import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Video from '../../videos/video.mp4';
import { CoverContainer, CoverBg, VideoBg, CoverContent, CoverH1, CoverP, CoverBtnWrapper, ArrowForward, ArrowRight } from './CoverElements';
import { Button } from '../ButtonElements';
import t3 from '../../assets/t3.jpg';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { IconButton, Box, Typography } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

const CoverSection = () => {
  const [hover, setHover] = useState(false);
  const [heroType, setHeroType] = useState('static'); // 'static' or 'carousel'
  const [heroImage, setHeroImage] = useState(null);
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHeroSettings();
  }, []);

  useEffect(() => {
    if (heroType === 'static') {
      fetchActiveHeroImage();
    } else if (heroType === 'carousel') {
      fetchCarouselSlides();
    }
  }, [heroType]);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    if (heroType === 'carousel' && carouselSlides.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlideIndex((prevIndex) =>
          (prevIndex + 1) % carouselSlides.length
        );
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [heroType, carouselSlides.length]);

  const fetchHeroSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/hero-images/settings`);
      setHeroType(response.data.heroType);
    } catch (error) {
      console.log('Failed to fetch hero settings, defaulting to static');
      setHeroType('static');
    }
  };

  const fetchActiveHeroImage = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/hero-images/active`);
      setHeroImage(response.data.heroImage);
    } catch (error) {
      console.log('No active hero image found, using default');
    } finally {
      setLoading(false);
    }
  };

  const fetchCarouselSlides = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/carousel/active`);
      setCarouselSlides(response.data.slides || []);
    } catch (error) {
      console.log('Failed to fetch carousel slides');
      setCarouselSlides([]);
    } finally {
      setLoading(false);
    }
  };

  const onHover = () => {
    setHover(!hover);
  };

  const handlePrevSlide = () => {
    setCurrentSlideIndex((prevIndex) =>
      prevIndex === 0 ? carouselSlides.length - 1 : prevIndex - 1
    );
  };

  const handleNextSlide = () => {
    setCurrentSlideIndex((prevIndex) =>
      (prevIndex + 1) % carouselSlides.length
    );
  };

  // Determine background image based on hero type
  let backgroundImage = t3;
  let slideTitle = '';
  let slideDescription = '';

  if (heroType === 'static' && heroImage) {
    backgroundImage = `${API_BASE_URL}${heroImage.imageUrl}`;
  } else if (heroType === 'carousel' && carouselSlides.length > 0) {
    const currentSlide = carouselSlides[currentSlideIndex];
    backgroundImage = `${API_BASE_URL}${currentSlide.imageUrl}`;
    slideTitle = currentSlide.title;
    slideDescription = currentSlide.description;
  }

  return (
    <CoverContainer>
      <CoverBg>
        <img
          src={backgroundImage}
          alt={heroType === 'static' ? (heroImage ? heroImage.title : "Hero Background") : slideTitle}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
            transition: 'opacity 0.5s ease-in-out'
          }}
        />

        {/* Dark overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        />
      </CoverBg>

      <CoverContent>
        {heroType === 'static' ? (
          <>
            <CoverH1>Liberia Motocyle & Tricyle Security Service</CoverH1>
            <CoverP>Sign up now to get your Motocyle and Tricyle secure. <span style={{ color: "#ffffff", fontWeight: "bold" }}>Ride With Ease</span></CoverP>
          </>
        ) : (
          <>
            <CoverH1>{slideTitle || 'Liberia Motocyle & Tricyle Security Service'}</CoverH1>
            <CoverP>{slideDescription || 'Sign up now to get your Motocyle and Tricyle secure.'}</CoverP>
          </>
        )}

        <CoverBtnWrapper>
          <Button to="signup" onMouseEnter={onHover} onMouseLeave={onHover} primary='true' dark='true'>
            Get started {hover ? <ArrowForward /> : <ArrowRight />}
          </Button>
        </CoverBtnWrapper>
      </CoverContent>

      {/* Carousel Controls - Outside CoverContent */}
      {heroType === 'carousel' && carouselSlides.length > 1 && (
        <>
          {/* Left Arrow - Far Left Edge of Section */}
          <IconButton
            onClick={handlePrevSlide}
            sx={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.2)',
              zIndex: 10,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.4)' },
              fontSize: '2rem',
              padding: 1.5,
              borderRadius: '4px'
            }}
          >
            <ChevronLeft fontSize="large" />
          </IconButton>

          {/* Right Arrow - Far Right Edge of Section */}
          <IconButton
            onClick={handleNextSlide}
            sx={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.2)',
              zIndex: 10,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.4)' },
              fontSize: '2rem',
              padding: 1.5,
              borderRadius: '4px'
            }}
          >
            <ChevronRight fontSize="large" />
          </IconButton>

          {/* Dot Indicators - Far Bottom of Section */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1.5,
              zIndex: 10
            }}
          >
            {carouselSlides.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentSlideIndex(index)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: index === currentSlideIndex ? 'white' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  border: '2px solid rgba(255,255,255,0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    transform: 'scale(1.2)'
                  }
                }}
              />
            ))}
          </Box>
        </>
      )}
    </CoverContainer>
  );
};

export default CoverSection;
